// UI actualitzada amb millors estils i experiència d'usuari
// Comentaris en castellà segons preferencia del usuario

import React, { useEffect, useMemo, useState } from "react"
import { Modal, Box, Button, Autocomplete, TextField, CircularProgress, Chip, Snackbar, Alert, IconButton, Tooltip, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteIcon from "@mui/icons-material/Delete"
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts"
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1"
import { listApi } from "../../services/Admin/request"
import { getApiManagerAssignedWorkers, getApiManagerAssignWorker, getApiManagerRemoveWorker } from "../../services/Admin/managers"

export default function ManageWorkersModal({ open, onClose, manager }) {
    const [assignedWorkers, setAssignedWorkers] = useState([])
    const [allWorkers, setAllWorkers] = useState([])
    const [selectedWorker, setSelectedWorker] = useState(null)
    const [loadingAssigned, setLoadingAssigned] = useState(false)
    const [loadingAll, setLoadingAll] = useState(false)
    const [assigning, setAssigning] = useState(false)
    const [snackbarOpen, setSnackbarOpen] = useState(false)

    // Cargar trabajadores asignados
    useEffect(() => {
        if (!manager?.id) return
        setLoadingAssigned(true)

        getApiManagerAssignedWorkers(manager.id)
            .then(res => setAssignedWorkers(res?.data?.data || []))
            .catch(() => setAssignedWorkers([]))
            .finally(() => setLoadingAssigned(false))
    }, [manager])

    // Cargar todos los trabajadores disponibles
    useEffect(() => {
        setLoadingAll(true)
        listApi("user")
            .then(resp => {
                const data = resp?.data?.data || []
                const filtered = data.filter(u => u.role === "ROLE_WORKER")
                setAllWorkers(filtered)
            })
            .catch(() => setAllWorkers([]))
            .finally(() => setLoadingAll(false))
    }, [])

    const candidateUsers = useMemo(() => {
        const assignedIds = new Set(assignedWorkers.map(u => u.id))
        return allWorkers.filter(u => !assignedIds.has(u.id))
    }, [allWorkers, assignedWorkers])

    // Asignar trabajador
    const handleAssignUser = async () => {
        if (!selectedWorker || assigning) return
        setAssigning(true)

        const optimistic = [...assignedWorkers, selectedWorker]
        setAssignedWorkers(optimistic)

        try {
            await getApiManagerAssignWorker({
                manager_id: manager.id,
                user_id: selectedWorker.id,
            })
            setSelectedWorker(null)
        } catch {
            setAssignedWorkers(prev => prev.filter(w => w.id !== selectedWorker.id))
        } finally {
            setAssigning(false)
        }
    }

    // Quitar trabajador
    const handleRemoveUser = async (userId) => {
        const prev = assignedWorkers
        setAssignedWorkers(prev.filter(u => u.id !== userId))

        try {
            await getApiManagerRemoveWorker({
                manager_id: manager.id,
                user_id: userId,
            })
        } catch {
            setAssignedWorkers(prev)
        }
    }

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "background.paper",
                boxShadow: 20,
                p: 4,
                minWidth: 480,
                maxWidth: 650,
                borderRadius: 3,
                position: "relative"
            }}>
                {/* Botón de cerrar arriba derecha */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        zIndex: 10
                    }}
                    aria-label="Cerrar"
                >
                    <CloseIcon />
                </IconButton>

                {/* HEADER sin iconos */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Typography variant="h6">{manager?.name}</Typography>
                    <Typography fontSize={14} mt={1} color="text.secondary">
                        Selecciona els treballadors que gestionarà
                    </Typography>
                </Box>

                {/* WORKERS LIST sin iconos */}
                <Box sx={{
                    border: "1px solid #e0e0e0",
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#fafafa",
                    maxHeight: 220,
                    overflowY: "auto",
                    mb: 2
                }}>
                    {loadingAssigned ? (
                        <CircularProgress size={20} />
                    ) : assignedWorkers.length > 0 ? (
                        assignedWorkers.map(user => (
                            <Chip
                                key={user.id}
                                label={`${user.name}  |  ${user.email}`}
                                sx={{
                                    bgcolor: "#e7effa",
                                    fontSize: "0.85em",
                                    mb: 1
                                }}
                                onDelete={() => handleRemoveUser(user.id)}
                                onClick={() => {
                                    navigator.clipboard.writeText(user.email)
                                    setSnackbarOpen(true)
                                }}
                            />
                        ))
                    ) : (
                        <Typography textAlign="center" color="text.secondary">
                            Encara no hi ha cap treballador assignat
                        </Typography>
                    )}
                </Box>

                {/* SELECTOR + BUTTON sin iconos */}
                <Box display="flex" gap={1}>
                    <Autocomplete
                        size="small"
                        fullWidth
                        options={candidateUsers}
                        value={selectedWorker}
                        loading={loadingAll}
                        onChange={(_, v) => setSelectedWorker(v)}
                        getOptionLabel={(o) => o ? `${o.name} (${o.email})` : ""}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Cerca un treballador…"
                                placeholder="Nom o correu"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: loadingAll
                                        ? <CircularProgress size={16} />
                                        : params.InputProps.endAdornment
                                }}
                            />
                        )}
                    />

                    <Button
                        variant="contained"
                        disabled={!selectedWorker || assigning}
                        onClick={handleAssignUser}
                    >
                        {assigning ? "Assignant…" : "Assignar"}
                    </Button>
                </Box>

                {/* SNACKBAR */}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={1800}
                    onClose={() => setSnackbarOpen(false)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert severity="success">Correu copiat!</Alert>
                </Snackbar>
            </Box>
        </Modal>
    )
}
